// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { safeJsonParse } from "@sc/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

// XNG-Change
export const InTheBoxSpesification = "in the box";
export const SpecialSpesification = "specifications";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Change
    attributesAccordionSection?: AccordionSectionPresentationProps;
    attributesWrapper?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, content },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    // XNG-Change
    const { inTheBoxData, specialSpesifications, otherSpecifications } =
        specifications?.reduce(
            (acc: any, specification) => {
                if (specification.name.toLowerCase() === InTheBoxSpesification) {
                    acc.inTheBoxData.push(specification);
                } else if (specification.name.toLowerCase() === SpecialSpesification) {
                    acc.specialSpesifications.push(specification);
                } else {
                    acc.otherSpecifications.push(specification);
                }
                return acc;
            },
            { inTheBoxData: [], specialSpesifications: [], otherSpecifications: [] },
        ) || {};

    let convertedSpesificationsList = [];
    if (specialSpesifications && specialSpesifications.length > 0) {
        const specialSpesificationsList = safeJsonParse(specialSpesifications[0].value);
        convertedSpesificationsList = specialSpesificationsList.map((specialSpesifications: any, index: any) => ({
            id: index,
            label: specialSpesifications.key,
            attributeValues: [
                {
                    value: specialSpesifications.value,
                    valueDisplay: specialSpesifications.value,
                },
            ],
        }));
    }

    const newAttributeTypes = [...(attributeTypes || []), ...convertedSpesificationsList];

    const renderingDocuments = showDocuments && documents && documents.length > 0;
    // XNG-Change
    const renderingAttributes = showAttributes && !!newAttributeTypes?.length;
    // XNG-Change
    const renderingSpecifications = !!otherSpecifications.length;
    const renderingInTheBoxSpecifications = !!inTheBoxData?.length;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications && !renderingInTheBoxSpecifications) {
        return null;
    }

    const documentsAccordion = renderingDocuments && (
        <ManagedAccordionSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                (!renderingSpecifications && !renderingAttributes)
            }
            {...styles.specificationsAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                            aria-label={document.name}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </ManagedAccordionSection>
    );

    const removeBorderForLastTwoItems = (newAttributeTypes && !(newAttributeTypes.length % 2)) || false;

    // XNG-Change
    const inTheBoxAccordion = renderingInTheBoxSpecifications && (
        <ManagedAccordionSection
            title={translate("In The Box")}
            initialExpanded={true}
            data-test-selector="productDetails_specifications_inTheBox"
            {...styles.specificationsAccordionSection}
        >
            {inTheBoxData?.map((inTheBoxData: any) => {
                return parse(transformContentHtml(inTheBoxData.htmlContent), parserOptions);
            })}
        </ManagedAccordionSection>
    );
    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={true}
            data-test-selector="productDetails_specifications_attributes"
            {...styles.attributesAccordionSection}
        >
            {/* {brand && (
                <StyledWrapper {...styles.attributeTypeWrapper}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                    <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                </StyledWrapper>
            )} */}
            {newAttributeTypes && (
                <StyledWrapper {...styles.attributesWrapper}>
                    {newAttributeTypes.map(attributeType => (
                        <StyledWrapper
                            {...styles.attributeTypeWrapper}
                            key={attributeType.id.toString()}
                            {...{ removeBorderForLastTwoItems }}
                        >
                            <Typography {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name}:&nbsp;
                            </Typography>
                            <Typography {...styles.attributeTypeValuesText}>
                                {attributeType
                                    .attributeValues!.map((attributeValue: any) => attributeValue.valueDisplay)
                                    .join(", ")}
                            </Typography>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            )}
        </ManagedAccordionSection>
    );
    // XNG-Change
    const descriptionAccordion = content && content.htmlContent && (
        <ManagedAccordionSection
            title={translate("Description")}
            initialExpanded={true}
            data-test-selector="productDetails_specifications_description"
            {...styles.specificationsAccordionSection}
        >
            <StyledWrapper>
                <div data-test-selector="productDetails_htmlContent">
                    {parse(transformContentHtml(content.htmlContent), parserOptions)}
                </div>
            </StyledWrapper>
        </ManagedAccordionSection>
    );
    return (
        <Accordion
            headingLevel={2}
            data-test-selector="productDetails_specifications"
            {...styles.specificationsAccordion}
        >
            {content && content.htmlContent && descriptionAccordion}
            {attributesFirst && attributesAccordion}
            {documentsFirst && documentsAccordion}
            {/* XNG-Change: specifications will be features only and will be displayed in one tab */}
            {renderingSpecifications && (
                <ManagedAccordionSection
                    initialExpanded={true}
                    key="specifications_features"
                    title={translate("Features")}
                    data-test-selector="productDetails_specifications_specification"
                    {...styles.specificationsAccordionSection}
                >
                    {/** XNG-Change:remove the in the box spesifications */}
                    <UnorderedList>
                        {otherSpecifications?.map((specification: any) => {
                            return (
                                <ListItem
                                    key={specification.id}
                                    data-test-selector="productDetails_specifications_wrapper"
                                >
                                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                                </ListItem>
                            );
                        })}
                    </UnorderedList>
                </ManagedAccordionSection>
            )}
            {renderingInTheBoxSpecifications && inTheBoxAccordion}
            {!attributesFirst && attributesAccordion}
            {!documentsFirst && documentsAccordion}
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
